import React from "react";

import { Button2 } from "@/components/UI2/atoms/Button";
import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import CloseIcon from "@/assets/images/icons2/icon--close.svg?component";
import { Heading2 } from "@/ui2/atoms/Heading";
import { ModalControlled2 } from "@/components/UI2/molecules/Modal";
import { TextField2 } from "@/components/UI2/molecules/TextField";

type EndCallDecisionModalProps = {
  isOpen: boolean;
  leftButtonLabel: string;
  leftHelperText: string;
  leftText: string;
  leftTitle: string;
  noShowHelperText?: string;
  noShowReason: string;
  onClose: (isOpen: boolean) => void;
  onLeftButtonClick: () => void;
  onNoShowClick: () => void;
  onNoShowReasonChange: (value: string) => void;
  title?: string;
};

// Extracted from RemoteVideo.js so the "End Call" decision modal (leave a
// draft note vs. record a no-show) can be reused with different copy/
// handlers per workflow (Behavioral/Prescribe, Clinical Assessment, and ACP
// Meeting all render this same shell today).
export function EndCallDecisionModal({
  isOpen,
  leftButtonLabel,
  leftHelperText,
  leftText,
  leftTitle,
  noShowHelperText = "This will exit the call and record a no show",
  noShowReason,
  onClose,
  onLeftButtonClick,
  onNoShowClick,
  onNoShowReasonChange,
  title = "End Call",
}: EndCallDecisionModalProps) {
  return (
    <ModalControlled2
      className="tw:max-w-4xl tw:min-w-175"
      isOpen={isOpen}
      onOpenChange={onClose}
      title={title}
    >
      <div className="tw:grid tw:grid-cols-2 tw:grid-rows-[auto_auto_1fr_auto_auto] tw:gap-x-8 tw:gap-y-2">
        <div className="tw:row-span-5 tw:grid tw:grid-rows-subgrid tw:justify-items-start">
          <Heading2 as="h2" size={1}>
            {leftTitle}
          </Heading2>
          <p className="body-sm">{leftText}</p>

          <Button2
            className="tw:row-start-4"
            icon={<CheckmarkIcon />}
            onPress={onLeftButtonClick}
            variant="secondary"
          >
            {leftButtonLabel}
          </Button2>

          <p className="body-xs tw:row-start-5">{leftHelperText}</p>
        </div>

        <div className="tw:row-span-5 tw:grid tw:grid-rows-subgrid tw:justify-items-start">
          <Heading2 as="h2" size={1}>
            Patient Did Not Attend
          </Heading2>

          <p className="body-sm">
            If your patient did not attend the call you can end the call here. Recording a{" "}
            <b>No-Show</b> on the session.
          </p>

          <TextField2
            aria-label="No Show Reason"
            className="tw:my-2 tw:w-full"
            mode="input"
            onChange={onNoShowReasonChange}
            placeholder="No Show Reason"
            value={noShowReason}
          />

          <Button2
            className="tw:row-start-4"
            icon={<CloseIcon />}
            onPress={onNoShowClick}
            variant="destructive-secondary"
          >
            No-Show
          </Button2>

          <p className="body-xs tw:row-start-5">{noShowHelperText}</p>
        </div>
      </div>
    </ModalControlled2>
  );
}
